import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const CAPSTONE = '#E85D75';
const TEXT_MUTED = '#5a5f7d';

interface FlowNode {
  label: string;
  x: number;
  y: number;
  h: number;
  w: number;
}

interface FlowLink {
  sourceY: number;
  targetY: number;
  thickness: number;
  targetIndex: number;
}

export function AttentionFlowSankey() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Source node ("You")
    const sourceNode: FlowNode = { label: 'You', x: 30, y: 30, h: 120, w: 22 };

    // Sink nodes
    const sinkNodes: FlowNode[] = [
      { label: 'Notifications', x: 248, y: 20, h: 44, w: 18 },
      { label: 'Feed', x: 248, y: 78, h: 52, w: 18 },
      { label: 'Messages', x: 248, y: 144, h: 36, w: 18 },
    ];

    // Flow links with varying thickness
    const links: FlowLink[] = [
      { sourceY: 50, targetY: 42, thickness: 18, targetIndex: 0 },
      { sourceY: 90, targetY: 104, thickness: 28, targetIndex: 1 },
      { sourceY: 135, targetY: 162, thickness: 12, targetIndex: 2 },
    ];

    // Draw source node
    const sourceGroup = svg.append('g');
    sourceGroup
      .append('rect')
      .attr('x', sourceNode.x)
      .attr('y', sourceNode.y)
      .attr('width', sourceNode.w)
      .attr('height', sourceNode.h)
      .attr('rx', 4)
      .attr('fill', CAPSTONE)
      .attr('opacity', 0.9);

    sourceGroup
      .append('text')
      .attr('x', sourceNode.x + sourceNode.w / 2)
      .attr('y', sourceNode.y - 8)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .text(sourceNode.label);

    // Draw sink nodes
    const sinkGroup = svg.append('g');
    sinkNodes.forEach((node) => {
      sinkGroup
        .append('rect')
        .attr('x', node.x)
        .attr('y', node.y)
        .attr('width', node.w)
        .attr('height', node.h)
        .attr('rx', 3)
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0.7);

      sinkGroup
        .append('text')
        .attr('x', node.x + node.w + 6)
        .attr('y', node.y + node.h / 2 + 4)
        .attr('text-anchor', 'start')
        .attr('font-size', 8)
        .attr('class', 'viz-text-muted')
        .text(node.label);
    });

    // Draw flow paths
    const flowGroup = svg.append('g');

    // Create gradient for flow
    const defs = svg.append('defs');

    links.forEach((link, i) => {
      const gradId = `flow-grad-${i}`;
      const grad = defs.append('linearGradient')
        .attr('id', gradId)
        .attr('x1', '0%').attr('x2', '100%');
      grad.append('stop').attr('offset', '0%').attr('stop-color', CAPSTONE).attr('stop-opacity', 0.6);
      grad.append('stop').attr('offset', '100%').attr('stop-color', TEXT_MUTED).attr('stop-opacity', 0.4);

      const sx = sourceNode.x + sourceNode.w;
      const tx = sinkNodes[link.targetIndex].x;
      const halfThick = link.thickness / 2;

      const pathData = `
        M ${sx},${link.sourceY - halfThick}
        C ${sx + 80},${link.sourceY - halfThick}
          ${tx - 80},${link.targetY - halfThick}
          ${tx},${link.targetY - halfThick}
        L ${tx},${link.targetY + halfThick}
        C ${tx - 80},${link.targetY + halfThick}
          ${sx + 80},${link.sourceY + halfThick}
          ${sx},${link.sourceY + halfThick}
        Z
      `;

      flowGroup
        .append('path')
        .attr('d', pathData)
        .attr('fill', `url(#${gradId})`)
        .attr('opacity', 0)
        .transition()
        .duration(500)
        .delay(i * 200)
        .attr('opacity', 1);
    });

    // Animate pulsing flow particles
    const particleGroup = svg.append('g');

    function createParticle(linkIndex: number) {
      const link = links[linkIndex];
      const sx = sourceNode.x + sourceNode.w;
      const tx = sinkNodes[link.targetIndex].x;

      const particle = particleGroup
        .append('circle')
        .attr('r', link.thickness / 6)
        .attr('fill', CAPSTONE)
        .attr('opacity', 0.8)
        .attr('cx', sx)
        .attr('cy', link.sourceY);

      particle
        .transition()
        .duration(1500 + Math.random() * 500)
        .ease(d3.easeQuadInOut)
        .attrTween('cx', () => {
          return (t: number) => {
            const x = sx + (tx - sx) * t;
            return String(x);
          };
        })
        .attrTween('cy', () => {
          return (t: number) => {
            const y = link.sourceY + (link.targetY - link.sourceY) * t;
            return String(y);
          };
        })
        .attr('opacity', 0.2)
        .remove();
    }

    // Periodically spawn particles
    const interval = d3.interval(() => {
      const linkIdx = Math.floor(Math.random() * links.length);
      createParticle(linkIdx);
    }, 400);

    // Animate "You" node shrinking to show attention leaking
    const shrinkTimer = d3.timeout(() => {
      sourceGroup
        .select('rect')
        .transition()
        .duration(3000)
        .ease(d3.easeLinear)
        .attr('height', sourceNode.h * 0.45)
        .attr('y', sourceNode.y + sourceNode.h * 0.275)
        .attr('opacity', 0.5);
    }, 1000);

    return () => {
      interval.stop();
      shrinkTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-mobile-screen" /> Phone &mdash; Attention Economy
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 200" />
      <p className="viz-container__caption">
        What captures attention becomes valuable.
      </p>
    </div>
  );
}
